import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import MartyrCard from '../components/MartyrCard';
import FeatureCard from '../components/FeatureCard';
import ImageWithFallback from '../components/ImageWithFallback';
import { martyrsApi, writingsApi, poemsApi, mediaApi, tributesApi } from '../services/api';
import { excerpt } from '../utils/place';
import { toYouTubeEmbed } from '../utils/youtube';

const HomePage = () => {
  const [martyrs, setMartyrs] = useState([]);
  const [writings, setWritings] = useState([]);
  const [poems, setPoems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tributes, setTributes] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [martyrsData, writingsData, poemsData, photosData, statsData, tributesData] = await Promise.all([
          martyrsApi.getAll({ page: 1, limit: 6 }),
          writingsApi.getAll({ limit: 3 }),
          poemsApi.getAll({ limit: 3 }),
          mediaApi.getAll({ media_type: 'image', limit: 8 }),
          martyrsApi.getStats().catch(() => null),
          tributesApi.getRecent({ limit: 3 }).catch(() => ({ tributes: [] }))
        ]);
        setMartyrs(martyrsData.martyrs || []);
        setWritings(writingsData.writings || []);
        setPoems(poemsData.poems || []);
        setPhotos(photosData.media || []);
        setStats(statsData);
        setTributes(tributesData.tributes || []);
      } catch (error) {
        setMartyrs([]);
      }

      try {
        const response = await fetch('/api/videos/recent?limit=3');
        if (response.ok) {
          const data = await response.json();
          setVideos(data.videos || []);
        }
      } catch (error) {
        setVideos([]);
      }
    };
    load();
  }, []);

  const featured = martyrs[0];
  const rest = martyrs.slice(1, 5);

  return (
    <Layout>
      <Seo title="الرئيسية" />

      <section className="relative overflow-hidden bg-gradient-to-b from-primary-950 via-primary-900 to-stone-950 px-4 pb-20 pt-16 text-white">
        <div className="museum-grid absolute inset-0 opacity-40" />
        <div className="relative container-responsive grid items-center gap-12 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <p className="text-xs tracking-[0.4em] text-gold-300">متحف رقمي للذكرى</p>
            <h1 className="text-4xl font-extrabold leading-tight md:text-6xl">أرشيف الشهداء</h1>
            <p className="max-w-xl text-lg leading-relaxed text-white/75">
              توثيق موقّر لسيرة من ضحّوا: وجوههم، كلماتهم، ما كُتب عنهم، والقصائد التي خُلدوا بها.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/martyrs" className="rounded-full bg-gold-500 px-6 py-3 font-semibold text-primary-950 hover:bg-gold-400">
                بطاقات الشهداء
              </Link>
              <Link to="/biography" className="rounded-full border border-white/30 px-6 py-3 hover:bg-white/10">
                السيرة الذاتية
              </Link>
            </div>
          </motion.div>
          {featured && (
            <div className="max-w-xl lg:mr-auto">
              <MartyrCard martyr={featured} featured />
            </div>
          )}
        </div>
      </section>

      {stats && (
        <section className="container-responsive -mt-10 relative z-10">
          <div className="grid grid-cols-2 gap-3 rounded-3xl border border-gold-500/20 bg-white p-4 shadow-large dark:bg-stone-900 md:grid-cols-4">
            {[
              { label: 'الشهداء', value: stats.totalMartyrs },
              { label: 'كتابات', value: stats.totalWritings || 0 },
              { label: 'قصائد', value: stats.totalPoems || 0 },
              { label: 'كلمات الرثاء', value: stats.totalTributes || 0 }
            ].map((item) => (
              <div key={item.label} className="rounded-2xl bg-stone-50 p-4 text-center dark:bg-stone-800">
                <p className="text-3xl font-bold text-primary-900 dark:text-gold-400">{item.value}</p>
                <p className="text-sm text-stone-500">{item.label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="container-responsive py-16">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="text-xs tracking-[0.3em] text-gold-700">أبرز الذكريات</p>
            <h2 className="text-3xl font-bold">بطاقات الشهداء</h2>
          </div>
          <Link to="/martyrs" className="text-sm font-semibold text-gold-700">عرض الأرشيف ←</Link>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {rest.map((martyr) => (
            <MartyrCard key={martyr.id} martyr={martyr} />
          ))}
        </div>
      </section>

      <section className="container-responsive pb-16">
        <h2 className="mb-8 text-3xl font-bold">أقسام الأرشيف</h2>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          <FeatureCard to="/guestbook" icon="❝" title="قالوا عن الشهيد" description="كلمات الزوار والرثاء في سجل الذكرى." />
          <FeatureCard to="/gallery" icon="▣" title="المعرض" description="الصور، وكل فيديوهات الموقع في مكان واحد." />
          <FeatureCard to="/writings" icon="📚" title="كتب عنه" description="كتابات ومقالات عامة، وما كُتب عن شهداء محددين." />
          <FeatureCard to="/poems" icon="✎" title="القصائد" description="قصائد عامة وقصائد الرثاء المرتبطة بالشهداء." />
        </div>
      </section>

      {(writings.length > 0 || poems.length > 0) && (
        <section className="bg-stone-100 py-16 dark:bg-stone-900">
          <div className="container-responsive grid gap-10 lg:grid-cols-2">
            <div>
              <div className="mb-6 flex justify-between">
                <h2 className="text-2xl font-bold">آخر الكتابات</h2>
                <Link to="/writings" className="text-gold-700">عرض المزيد</Link>
              </div>
              <div className="space-y-4">
                {writings.map((item) => (
                  <Link key={item.id} to={`/writings/${item.id}`} className="block rounded-2xl bg-white p-5 shadow-soft dark:bg-stone-800">
                    <h3 className="font-bold text-primary-900 dark:text-white">{item.title}</h3>
                    <p className="text-sm text-stone-500">{item.author}{item.martyr_name_ar ? ` — عن ${item.martyr_name_ar}` : ''}</p>
                    {item.content && <p className="mt-2 text-stone-600 dark:text-stone-300">{excerpt(item.content, 110)}</p>}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-6 flex justify-between">
                <h2 className="text-2xl font-bold">آخر القصائد</h2>
                <Link to="/poems" className="text-gold-700">عرض المزيد</Link>
              </div>
              <div className="space-y-4">
                {poems.map((item) => (
                  <Link key={item.id} to={`/poems/${item.id}`} className="block rounded-2xl bg-white p-5 text-center shadow-soft dark:bg-stone-800">
                    <h3 className="font-bold">{item.title}</h3>
                    {item.poet_name && (
                      <p className="poet-name text-gold-700">الشاعر: {item.poet_name}</p>
                    )}
                    {item.martyr_name_ar && <p className="text-sm text-stone-500">عن الشهيد {item.martyr_name_ar}</p>}
                    <p className="poem-verse mt-3 line-clamp-3 text-lg">{item.content}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {photos.length > 0 && (
        <section className="container-responsive py-16">
          <div className="mb-8 flex justify-between">
            <h2 className="text-3xl font-bold">صور مميزة</h2>
            <Link to="/gallery/photos" className="text-gold-700">معرض الصور ←</Link>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {photos.slice(0, 8).map((photo) => (
              <Link key={photo.id} to={`/martyr/${photo.martyr_id}`} className="group overflow-hidden rounded-2xl">
                <ImageWithFallback src={photo.file_url} alt={photo.title} className="h-40 w-full object-cover transition duration-500 group-hover:scale-105" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {videos.length > 0 && (
        <section className="container-responsive pb-16">
          <div className="mb-8 flex justify-between">
            <h2 className="text-3xl font-bold">فيديو مميز</h2>
            <Link to="/gallery/videos" className="text-gold-700">معرض الفيديو ←</Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {videos.map((video) => (
              <article key={video.id} className="overflow-hidden rounded-3xl bg-black shadow-large">
                <div className="aspect-video">
                  <iframe
                    title={video.title}
                    src={toYouTubeEmbed(video.youtubeLink)}
                    className="h-full w-full"
                    loading="lazy"
                    allowFullScreen
                  />
                </div>
                <h3 className="p-4 text-white">{video.title}</h3>
              </article>
            ))}
          </div>
        </section>
      )}

      {tributes.length > 0 && (
        <section className="container-responsive pb-20">
          <div className="mb-8 flex justify-between">
            <h2 className="text-3xl font-bold">قالوا عن الشهيد</h2>
            <Link to="/guestbook" className="text-gold-700">سجل الزوار ←</Link>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {tributes.map((item) => (
              <blockquote key={item.id} className="rounded-3xl border border-gold-500/20 bg-white p-6 dark:bg-stone-900">
                <p className="leading-relaxed text-stone-700 dark:text-stone-300">“{excerpt(item.message, 160)}”</p>
                <footer className="mt-4 text-sm text-gold-700">{item.visitor_name} — {item.martyr_name}</footer>
              </blockquote>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
};

export default HomePage;
